import { AgCheckBoxCellRendererComponent } from 'components/AgGrid/AgCheckBoxCellRendererComponent';
import { suppressKeyboardEventForInput } from 'components/AgGrid/utils';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { UseFormClearErrors, UseFormSetError, UseFormSetValue } from 'react-hook-form/dist/types/form';

import { VolumeCellRendererComponent } from '../../MoveVolumesBetweenSeasons/components/VolumeCellRendererComponent';

import type { IMoveVolumesBetweenSeasonsForObjectItem } from './useUerWorkOrderChangeObjectsForReminders';

interface IUseMoveVolumesBetweenSeasonsForObjectColumns {
    setValue: UseFormSetValue<any>;
    setError: UseFormSetError<any>;
    clearErrors: UseFormClearErrors<any>;
    volumesWithErrors: string[];
    items?: IMoveVolumesBetweenSeasonsForObjectItem[];
    isOpen: boolean;
}

const cellStyle = (params: any) => (!params.data.parentId ? { background: 'var(--color-bg-stripe)' } : undefined);

export const useMoveVolumesBetweenSeasonsForObjectColumns = (props: IUseMoveVolumesBetweenSeasonsForObjectColumns) => {
    const { setValue, setError, clearErrors, volumesWithErrors, items, isOpen } = props;

    const idsToDuplicate = useMemo(() => items?.filter((i) => i.parentId)?.map((i) => i.id), [items]);
    const parentId = useMemo(() => items?.find((i) => !i.parentId)?.id, [items]);

    const [checkedIds, setCheckedIds] = useState<string[]>([]);

    const onCheckChange = useCallback((checked: string[]) => {
        setCheckedIds(checked);
    }, []);

    const isDisabledGetter = useCallback((id: string) => !checkedIds.includes(id), [checkedIds]);

    const checkboxPropsGetter = useCallback(
        (id: any) => ({
            checked: checkedIds.includes(id),
            intermediate: false,
            disabled:
                id === parentId
                    ? checkedIds.length && !checkedIds.includes(id)
                    : parentId && checkedIds.includes(parentId),
        }),
        [checkedIds, parentId],
    );

    const canRunDuplication = useCallback((data: any) => !data.parentId, []);

    useEffect(() => {
        items?.forEach((item) => {
            if (item.id && !checkedIds.includes(item.id)) {
                setValue(`volume_${item.id}`, null);
                clearErrors(`volume_${item.id}`);
            }
        });
    }, [setValue, clearErrors, items, checkedIds]);

    useEffect(() => {
        if (isOpen) {
            setCheckedIds([]);
        }
    }, [isOpen]);

    return useMemo<(ColDef | ColGroupDef)[]>(
        () => [
            {
                field: 'checkbox',
                headerName: '',
                width: 48,
                cellRenderer: AgCheckBoxCellRendererComponent,
                cellRendererParams: {
                    checkedIds,
                    onCheckChange,
                    checkboxPropsGetter,
                },
                cellStyle,
            },
            {
                width: 520,
                headerName: 'Наименование',
                field: 'name',
                cellStyle,
            },
            {
                width: 162,
                headerName: 'Объем, %',
                field: 'volume',
                cellRenderer: VolumeCellRendererComponent,
                cellRendererParams: {
                    onChange: setValue,
                    idsToDuplicate,
                    setError,
                    clearErrors,
                    volumesWithErrors,
                    errorMessage: 'Введенное значение превышает 100%',
                    decimalScale: 0,
                    isDisabledGetter,
                    canRunDuplication,
                    checkedIds,
                    isChangeDuplicate: false,
                },
                suppressKeyboardEvent: suppressKeyboardEventForInput,
                cellStyle,
            },
        ],
        [
            canRunDuplication,
            checkboxPropsGetter,
            checkedIds,
            clearErrors,
            idsToDuplicate,
            isDisabledGetter,
            onCheckChange,
            setError,
            setValue,
            volumesWithErrors,
        ],
    );
};
